import { toast, type ToastContainerOptions } from 'vue3-toastify';
import type { Options, ToastOptions } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

const defaultOptions: ToastContainerOptions = {
  position: 'top-center',
  theme: 'auto',
  autoClose: 1000,
  icon: true,
  limit: 2,
  // transition: 'flip',
  clearOnUrlChange: false,
  dangerouslyHTMLString: true,
  style: {
    width: 'fit-content',
  }
}

// Map para rastrear toasts ativos e seus IDs
const activeToasts = new Map<string, string | number>();

//? Fluxos em lote mostram o erro de cada registro na própria tela (status + mensagem na linha).
//? Sem isto, o handler global do @sieg-ad/fetch empilha um toast por requisição que falha.
//? Contador (não booleano) para suportar chamadas aninhadas.
let silenciados = 0;

/** Executa a ação sem abrir toasts — o chamador se responsabiliza por exibir o resultado. */
export const semToasts = async <T>(acao: () => Promise<T>): Promise<T> => {
  silenciados++;

  try {
    return await acao();
  }
  finally {
    silenciados = Math.max(0, silenciados - 1);
  }
}

// Função auxiliar para gerar um ID único baseado na mensagem
const getToastId = (message: string, type: string) => {
  return `${type}-${message}`;
}

// Função auxiliar para criar toast sem duplicatas, resetando o timer se já existir
const createToast = (
  type: 'success' | 'error' | 'info' | 'warning',
  message: string,
  options?: Options
) => {
  if (silenciados > 0) return;

  const toastKey = getToastId(message, type);

  // Se já existe um toast com essa mensagem, tenta atualizar para resetar o timer
  if (activeToasts.has(toastKey)) {
    const existingToastId = activeToasts.get(toastKey);
    
    // Tenta atualizar o toast existente (reseta o timer automaticamente)
    try {
      toast.update(existingToastId!, options);
      return existingToastId;
    } 
    catch {
      // Se update falhar, remove e recria
      toast.remove(existingToastId);
      activeToasts.delete(toastKey);
    }
  }
  
  const toastOptions: ToastOptions = {
    ...options,
    toastId: toastKey,
    onClose: () => {
      activeToasts.delete(toastKey);
    },
  };
  
  // Cria o toast e armazena seu ID
  const toastId = toast[type](message, toastOptions);
  activeToasts.set(toastKey, toastId);
  
  return toastId;
}

export const successToast = (message: string, options?: Options) => {
  return createToast('success', message, { 
    ...defaultOptions,
    ...options, 
    autoClose: 500,
    position: 'top-center',
    theme: getTheme() 
  });
}

export const errorToast = (message: string, options?: Options) => {
  return createToast('error', message, {
    autoClose: 4000,
    ...defaultOptions,
    ...options,
    theme: getTheme(),
  });
}

export const infoToast = (message: string, options?: Options) => {
  return createToast('info', message, { 
    autoClose: 4000,
    ...defaultOptions,
    ...options, 
    theme: getTheme() 
  });
}

export const warnToast = (message: string, options?: Options) => {
  return createToast('warning', message, {
    autoClose: 4000, 
    ...defaultOptions,
    ...options, 
    theme: getTheme() 
  });
}

const getTheme = () => document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';